/**
 * The position, scale and rotation of the customization text on the product
 * line picture, with a live preview. Arrow keys nudge the picture while the
 * mouse is over it. Disabled for the "shoe sizes" display.
 */
import {
  useEffect,
  useRef,
  useState,
  type SubmitEvent,
  type MouseEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearchMinus, faSearchPlus } from '@fortawesome/free-solid-svg-icons';
import { appConfigQueryKey, fetchAppConfig } from '@/shared/config/app-config';
import {
  filterMatrix,
  fontWithWeight,
  getTextZoneStyle,
} from '@/shared/lib/text-zone';
import { Dialog } from '@/shared/ui/Dialog';
import { CircularText } from '@/shared/ui/CircularText';
import { PictureField } from '@/shared/ui/PictureField';
import { usePicture } from '@/shared/ui/use-picture';
import {
  deleteProductLinePicture,
  productLineQueryOptions,
  updateProductLine,
  uploadProductLinePicture,
  type ProductLine,
} from './api';
import { productLinePictureUrl } from './product-line-form';
import {
  DEFAULTS,
  productLineToXy,
  validateXy,
  xyToProductLine,
  type XyValues,
} from './xy-form';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface ProductLineXyDialogProps {
  item: ProductLine;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const PREVIEW_TEXT = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const ZOOM_STEP = 0.05;

const fieldTestId = (id: keyof XyValues) =>
  `product-line-xy-form-${kebabCase(id)}`;

const numberField = (
  id: keyof XyValues,
  label: string,
  placeholder: string,
  values: XyValues,
  set: (key: keyof XyValues, value: number) => void,
  error: string | undefined,
) => (
  <div className="mb-2">
    <label className="field-label" htmlFor={`xy-${id}`}>
      {label}
    </label>
    <input
      id={`xy-${id}`}
      data-testid={fieldTestId(id)}
      className="field"
      name={id}
      type="number"
      placeholder={placeholder}
      value={Number.isNaN(values[id]) ? '' : values[id]}
      onChange={(event) => set(id, event.target.valueAsNumber)}
    />
    <small hidden={!error} data-testid={`${fieldTestId(id)}-error`}>
      {error}
    </small>
  </div>
);

export function ProductLineXyDialog({
  item,
  onClose,
}: Readonly<ProductLineXyDialogProps>) {
  const queryClient = useQueryClient();
  const productLine = useQuery(productLineQueryOptions(item.id));
  // Edits start from the fetched record.
  const [edits, setEdits] = useState<XyValues | null>(null);
  const values =
    edits ?? (productLine.data ? productLineToXy(productLine.data) : DEFAULTS);
  const setValues = (update: (prev: XyValues) => XyValues) =>
    setEdits((prev) =>
      update(
        prev ??
          (productLine.data ? productLineToXy(productLine.data) : DEFAULTS),
      ),
    );
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.35);
  const picture = usePicture();
  const pictureRowRef = useRef<HTMLDivElement>(null);
  const mouseOver = useRef<EventTarget | null>(null);

  // Arrow keys move the picture while the mouse is over it.
  useEffect(() => {
    const onMouseOver = (event: Event) => {
      mouseOver.current = event.target;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const active = document.activeElement;
      if (
        !active ||
        !(
          active.tagName === 'DIALOG' ||
          active.classList.contains('field-file-input')
        )
      )
        return;
      const delta: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      const move = delta[event.key];
      if (!move) return;
      const target = mouseOver.current;
      if (!(target instanceof Node) || !pictureRowRef.current?.contains(target))
        return;
      const base = productLine.data
        ? productLineToXy(productLine.data)
        : DEFAULTS;
      setEdits((prev) => ({
        ...(prev ?? base),
        customX: (prev ?? base).customX + move[0],
        customY: (prev ?? base).customY + move[1],
      }));
      event.preventDefault();
    };
    document.addEventListener('mouseover', onMouseOver);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [productLine.data]);

  const set = (key: keyof XyValues, value: number) =>
    setValues((prev) => ({ ...prev, [key]: value }));
  const errors = submitted ? validateXy(values) : {};

  const save = useMutation({
    mutationFn: async () => {
      try {
        await updateProductLine({
          id: item.id,
          customPictureUpdatedAt: Date.now(),
          ...xyToProductLine(values),
        });
      } catch (error) {
        throw new Error('Failed to update product line', { cause: error });
      }
      try {
        if (picture.touched) {
          if (picture.file)
            await uploadProductLinePicture(item.id, picture.file, 'custom');
          else await deleteProductLinePicture(item.id, 'custom');
        }
      } catch (error) {
        throw new Error('Failed to upload/remove product line pictures', {
          cause: error,
        });
      }
      await fetchAppConfig();
      await queryClient.invalidateQueries({ queryKey: appConfigQueryKey });
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (Object.keys(validateXy(values)).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const zoomBy = (event: MouseEvent, step: number) => {
    event.preventDefault();
    event.stopPropagation();
    setZoom((current) => current + step);
  };

  const textureImg = productLinePictureUrl(item.id, 'texture') ?? '';
  const latin = item.keyboard?.latin;
  const previewText = PREVIEW_TEXT.slice(0, Number(latin?.maxChars) || 13);
  const disabled =
    (productLine.data?.sizeDisplay ?? item.sizeDisplay) === 'shoeSize';
  const textZoneStyle = {
    ...getTextZoneStyle({
      textProps: xyToProductLine(values).textProps,
      color: '#ffffff',
      shadowType: item.shadowType,
      font: fontWithWeight(latin?.font, latin?.fontWeight),
    }),
    backgroundImage: `url(${textureImg})`,
  };
  const custom = xyToProductLine(values).imageProps.custom;
  const imageStyle = {
    transform: `translate(${custom.offsetX ?? 0}px, ${custom.offsetY ?? 0}px) scale(${(custom.scale ?? 100) / 100})`,
  };

  const preview = (src: string, testId?: string) => (
    <div
      className="dp-custom-image-container"
      style={{ transform: `scale(${zoom})` }}
    >
      <img src={src} alt="" style={imageStyle} data-testid={testId} />
      <div className="dp-custom-image-text-zone" style={textZoneStyle}>
        {values.textCurve ? (
          <CircularText
            text={previewText}
            textureImg={textureImg}
            radius={values.textCurve}
          />
        ) : (
          previewText
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      title="Product Line XY Edit"
      onClose={() => onClose('Cancel')}
      testId="product-line-xy-dialog"
    >
      <svg style={{ display: 'none' }} aria-hidden="true">
        <filter id="textZoneFilter">
          <feColorMatrix type="matrix" values={filterMatrix()} />
        </filter>
      </svg>
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className={`relative mb-1 ${disabled ? 'menu-disabled' : ''}`}>
          <h3 className="text-center">Customization view</h3>
          <div className="bs-row mb-2">
            <div className="col-span-6">
              {numberField(
                'customScale',
                'Scale',
                'Scale',
                values,
                set,
                errors.customScale,
              )}
              {numberField('customY', 'Y', 'Y', values, set, undefined)}
              {numberField('customX', 'X', 'X', values, set, undefined)}
            </div>
            <div className="col-span-6">
              {numberField(
                'textScale',
                'Zone Scale',
                'Scale',
                values,
                set,
                errors.textScale,
              )}
              {numberField('textY', 'Zone Y', 'Y', values, set, undefined)}
              {numberField('textX', 'Zone X', 'X', values, set, undefined)}
              <div className="bs-row mb-2">
                <div className="col-span-4">
                  {numberField(
                    'textRotate',
                    'Rotation',
                    'Degrees',
                    values,
                    set,
                    undefined,
                  )}
                </div>
                <div className="col-span-4">
                  {numberField(
                    'textCurve',
                    'Curvature',
                    'Pixels',
                    values,
                    set,
                    undefined,
                  )}
                </div>
                <div className="col-span-4">
                  {numberField(
                    'textLetterSpace',
                    'Letter spacing',
                    'Pixels',
                    values,
                    set,
                    undefined,
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className="bs-row justify-center"
            ref={pictureRowRef}
            id="custom-picture-row"
          >
            <div className="col-span-7 col-start-3">
              <PictureField
                id="product-line-custom"
                name="customPicture"
                label="Picture"
                picture={picture}
                serverUrl={productLinePictureUrl(item.id, 'custom')}
                renderPreview={preview}
                labelClassName="preview-frame"
                testId="product-line-xy-form-custom-picture"
              >
                <button
                  type="button"
                  className="zoom-in-icon"
                  aria-label="Zoom in"
                  onClick={(event) => zoomBy(event, ZOOM_STEP)}
                  data-testid="product-line-xy-form-zoom-in-button"
                >
                  <FontAwesomeIcon icon={faSearchPlus} />
                </button>
                <button
                  type="button"
                  className="zoom-out-icon"
                  aria-label="Zoom out"
                  onClick={(event) => zoomBy(event, -ZOOM_STEP)}
                  data-testid="product-line-xy-form-zoom-out-button"
                >
                  <FontAwesomeIcon icon={faSearchMinus} />
                </button>
              </PictureField>
            </div>
          </div>
        </div>
        {errorMessage && (
          <div
            className="alert mb-4"
            role="alert"
            data-testid="product-line-xy-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-2 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            disabled={save.isPending}
            data-testid="product-line-xy-form-save-button"
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
